<template>
  <!-- The mini statement, collapsed by default: this carton's own wallet
       movements. The «last movements before the invoice» ladder is gone (owner,
       carton-account revision R2). Every row is a wallet row, so a tap opens the
       same detail sheet as the wallet page. -->
  <section v-if="cartonRows.length" class="cms" data-block="statement">
    <div class="cms-section" data-section="carton">
      <button
        type="button"
        class="cms-toggle"
        :aria-expanded="cartonOpen ? 'true' : 'false'"
        @click="cartonOpen = !cartonOpen"
      >
        <span>{{ $t('box.totals.account.statement.carton') }}</span>
        <f7-icon :f7="cartonOpen ? 'chevron_up' : 'chevron_down'" size="14"></f7-icon>
      </button>
      <div v-if="cartonOpen" class="cms-rows">
        <button
          v-for="row in cartonRows"
          :key="row.transactionId"
          type="button"
          class="cms-row"
          data-row="statement-row"
          @click="emit('open-transaction', row.transactionId)"
        >
          <span class="cms-row-main">
            <span class="cms-row-title">{{ rowTitle(row) }}</span>
            <span class="cms-row-date">{{ rowDate(row) }}</span>
          </span>
          <span class="cms-row-amount" :class="{ 'cms-pos': row.amountCents > 0 }">
            {{ formatSignedCurrency(row.amountCents) }}
          </span>
        </button>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from '@/composables/useCurrency.js';
import { cartonRowTitle, formatCartonDate } from '@/utils/cartonAccount.js';

const props = defineProps({
  // The order box's `cartonAccount`.
  account: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['open-transaction']);

const { t, te, locale } = useI18n();
const { formatSignedCurrency } = useCurrency();

const cartonOpen = ref(false);

const statement = computed(() => props.account?.miniStatement ?? {});
const cartonRows = computed(() => (Array.isArray(statement.value.cartonRows) ? statement.value.cartonRows : []));

const rowTitle = (row) => cartonRowTitle(row?.title, t, te);
const rowDate = (row) => formatCartonDate(row?.at, locale.value) ?? '';
</script>

<style scoped>
.cms {
    border-bottom: 1px solid var(--poolit-border-color);
}

.cms-toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    min-height: 44px;
    padding: 10px 16px;
    border: 0;
    background: transparent;
    font-family: inherit;
    font-size: 13px;
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-primary-600);
    text-align: start;
    cursor: pointer;
}

.cms-rows {
    padding: 0 16px 10px;
}

.cms-row {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
    padding: 8px 0;
    border: 0;
    border-bottom: 1px solid var(--poolit-border-color);
    background: transparent;
    font-family: inherit;
    text-align: start;
    color: var(--poolit-text-primary);
    cursor: pointer;
}

.cms-row-main {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.cms-row-title {
    font-size: 13px;
    overflow-wrap: anywhere;
}

.cms-row-date {
    font-size: 11px;
    color: var(--poolit-text-tertiary);
    font-feature-settings: 'tnum' on, 'lnum' on;
}

.cms-row-amount {
    font-size: 13px;
    font-weight: var(--poolit-font-weight-semibold);
    font-feature-settings: 'tnum' on, 'lnum' on;
    white-space: nowrap;
}

.cms-pos {
    color: var(--poolit-success-600, #1b8a3b);
}
</style>
